import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { usePassProducts } from '../../../hooks/passes/usePassProducts'
import { useClubs } from '../../../hooks/clubs/useClubs'
import { useCourtOptions } from '../../../hooks/court/useCourtOptions'

import Icon from '../../../components/ui/Icon'
import Loading from '../../../components/loading/Loading'
import { Button } from '../../../components/buttons/Button'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import {
  PassAction,
  PassManagerCard,
} from '../../../components/authenticated/management/passProduct/passManagerCard/PassManagerCard'

import { useApp } from '../../../contexts/AppContext'
import { useConfirm } from '../../../contexts/ConfirmContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { ConfirmLayout } from '../../../types/confirm/confirm'
import { PassProduct } from '../../../types/booking/booking'
import { ClubModelMetadata } from '../../../types/court/court'
import { formatMinutes, formatMoney } from '../../../utils/booking/booking'

/**
 * Pass products. Deactivating stops new sales (pending purchases are still honoured); an emergency suspension makes
 * pending purchases fail with an automatic refund.
 */
export const ManagerPasses = () => {
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { t } = useTranslation(['passes', 'bookings'])
  const { list, updateStatus } = usePassProducts()
  const clubsHook = useClubs()
  const { courts } = useCourtOptions()
  const [clubs, setClubs] = useState<ClubModelMetadata[] | null>(null)
  const [products, setProducts] = useState<PassProduct[] | null>(null)

  const courtNames = useMemo(() => new Map(courts.map(court => [court.id, court.name])), [courts])

  const clubsById = useMemo(() => new Map((clubs ?? []).map(club => [club.id, club])), [clubs])

  /** The pass's club and its opening hours (the span of the card's timeline). */
  const clubInfo = (product: PassProduct) => {
    const club = product.clubId !== undefined ? clubsById.get(product.clubId) : undefined
    return club ? { clubName: club.name, openTime: club.openTime, closeTime: club.closeTime } : {}
  }

  const load = useCallback(() => {
    list(true)
      .then(result => setProducts(result ?? []))
      .catch(caught => showBanner({ kind: BannerKind.ERR, msg: (caught as Error).message }))
  }, [list, showBanner])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    clubsHook.clubs(50).then(page => setClubs(page?.content ?? []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const changeStatus = (product: PassProduct, status: { active?: boolean; suspended?: boolean }, warning?: string) => {
    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('passes.manager.status-confirm-title'),
      confirmLabel: t('passes.manager.status-confirm'),
      backLabel: t('bookings:booking-confirmation.back-label'),
      returnTo: '/m/passes',
      warning,
      itemCards: [
        {
          id: product.id!,
          title: product.name,
          details: [
            { label: t('passes.manager.card.price'), value: formatMoney(product.price, product.currency) },
            { label: t('passes.manager.card.minutes'), value: formatMinutes(product.includedMinutes) },
          ],
        },
      ],
      onConfirm: async () => {
        await updateStatus(product.id!, status)
        showBanner({ kind: BannerKind.OK, msg: t('passes.successfully-updated') })
      },
    })
  }

  const handleAction = (product: PassProduct, action: PassAction) => {
    switch (action) {
      case PassAction.EDIT:
        navigate(`/m/passes/${product.id}`)
        return
      case PassAction.ACTIVATE:
        changeStatus(product, { active: true })
        return
      case PassAction.DEACTIVATE:
        changeStatus(product, { active: false }, t('passes.manager.deactivate-warning'))
        return
      case PassAction.SUSPEND:
        changeStatus(product, { suspended: true }, t('passes.manager.suspend-warning'))
        return
      case PassAction.UNSUSPEND:
        changeStatus(product, { suspended: false })
        return
    }
  }

  return (
    <>
      <PageHeader
        title={{ eyebrow: t('passes.manager.eyebrow'), h1: t('passes.manager.title') }}
        sub={t('passes.manager.sub')}
        actions={[
          <Button
            key="new"
            label={t('passes.manager.new')}
            icon={<Icon name="plus" size={14} />}
            size="md"
            onClick={() => navigate('/m/passes/new')}
          />,
        ]}
      />
      {products === null ? (
        <Loading variant="inline" />
      ) : products.length === 0 ? (
        <div className="empty muted">{t('passes.manager.empty')}</div>
      ) : (
        <div className="row g-3">
          {products.map(product => (
            <div key={product.id} className="col-12">
              <PassManagerCard
                product={product}
                courtNames={courtNames}
                {...clubInfo(product)}
                onAction={handleAction}
              />
            </div>
          ))}
        </div>
      )}
    </>
  )
}
